import { useEffect, useMemo, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import {
  Button,
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogContent,
  DialogFooter,
  Input,
  Label,
  Select,
  Switch,
} from '@/components/ui';
import type {
  Automation,
  AutomationAction,
  AutomationTrigger,
  CreateAutomationInput,
} from '@/lib/automations-store';
import type { KanbanColumn } from '@/lib/use-kanban-columns';

import { AutomationSandboxDialog } from '../AutomationSandboxDialog';
import type { Deal } from '@/lib/comercial-types';
// FEAT-V2-032: builder de condicionais opcionais antes das actions.
import {
  ConditionsBuilder,
  type ConditionsConfig,
} from '@/components/automations/ConditionsBuilder';
// BUG-V2-058 / Sprint 3: contexto opcional pra pré-preencher trigger.
import type { AutomationContexto } from '../types';
// Sprint 3.1 (2026-05-26): bloco inline de actions (linhas 544-1345 ~800 LOC)
// substituído pelo componente shared AcoesEditor — reuso entre Workflow +
// Questionario + TaskType forms.
import { AcoesEditor } from '../AcoesEditor';

/**
 * WorkflowAutomationForm — wizard 3 passos pra criar/editar automação de
 * workflow (mesmo backend `/api/v1/automations`).
 *
 * Movido em Sprint 3 (BUG-V2-058) de `src/pages/menu/comercial/AutomationFormDialog.tsx`
 * pra shared component. Acessado via `<AutomationFormDialog contexto={...}>` shell.
 *
 * Passos:
 *   1. Quando — trigger type + coluna + (se idle) dias
 *   2. Então  — actions (notify_in_app real; whatsapp/email stub)
 *   3. Confirmar — nome auto-gerado (editável) + enable + botão Testar
 *
 * Zod no submit; validação inline + voltar passos preservando state. Não
 * fecha modal ao avançar passo (preserva form state). `onSubmit` recebe
 * `CreateAutomationInput` (sem workflowSlug — passado externo).
 *
 * **Contexto** (Sprint 3): `contexto` é metadata informativa neste form —
 * a pré-seleção da coluna passa via prop `initial` (caller monta a partir
 * do contexto se quiser). Pra trigger questionario_resposta, usar o wrapper
 * `QuestionarioAutomationForm` que pré-configura `initial` apropriadamente.
 */

// ─── Zod schemas (espelha backend) ────────────────────────────────────

const triggerSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('card.entered_column'),
    config: z.object({ columnSlug: z.string().min(1, 'Selecione uma coluna') }),
  }),
  z.object({
    type: z.literal('card.left_column'),
    config: z.object({ columnSlug: z.string().min(1, 'Selecione uma coluna') }),
  }),
  z.object({
    type: z.literal('card.idle_in_column'),
    config: z.object({
      columnSlug: z.string().min(1, 'Selecione uma coluna'),
      days: z.coerce.number().int().min(1, 'Mínimo 1 dia').max(365, 'Máximo 365 dias'),
    }),
  }),
]);

// BUG-V2-040 (2026-05-24): 7 actions cross-workflow + celebrar adicionadas
// ao dropdown. Schemas espelham os do backend em server/routes/automations.ts
// (Zod 1:1 com `AutomationAction` em server/types/automations.ts).
const cardAlvoEnum = z.enum(['pai', 'filho_recem_criado']);
const responsavelEstrategiaEnum = z.enum([
  'fixo',
  'workflow_owner',
  'round_robin',
  'least_loaded',
  'tag',
]);
const prioridadeCanonicaEnum = z.enum(['baixa', 'normal', 'alta', 'urgente']);

const actionSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('notify_in_app'),
    target: z.enum(['responsavel', 'criador', 'todos_no_card', 'user_specific']),
    userId: z.string().optional(),
    message: z.string().trim().min(1, 'Mensagem obrigatória').max(280),
  }),
  z.object({
    type: z.literal('send_whatsapp'),
    target: z.enum(['responsavel', 'cliente', 'lista']),
    lista: z.array(z.string()).optional(),
    message: z.string().trim().min(1, 'Mensagem obrigatória').max(1000),
  }),
  z.object({
    type: z.literal('send_email'),
    target: z.enum(['responsavel', 'cliente', 'lista']),
    lista: z.array(z.string()).optional(),
    subject: z.string().trim().min(1, 'Assunto obrigatório').max(160),
    body: z.string().trim().min(1, 'Corpo obrigatório').max(5000),
  }),
  // Fase B (2026-05-18)
  z.object({
    type: z.literal('create_task'),
    target: z.enum(['responsavel', 'criador', 'user_specific', 'round_robin']),
    userId: z.string().optional(),
    pool: z.array(z.string()).optional(),
    texto: z.string().trim().min(3, 'Mínimo 3 caracteres').max(500),
    prazoDiasUteis: z.coerce.number().int().min(1, 'Mínimo 1 dia útil').max(60, 'Máximo 60 dias úteis'),
  }),
  z.object({
    type: z.literal('move_card'),
    targetColumnSlug: z.string().min(1, 'Selecione coluna destino'),
    registrarAtividade: z.boolean().optional(),
  }),
  z.object({
    type: z.literal('assign_responsavel'),
    strategy: z.enum(['fixed', 'round_robin', 'least_loaded']),
    userId: z.string().optional(),
    pool: z.array(z.string()).optional(),
  }),
  // BUG-V2-040: 7 actions cross-workflow + celebrar (Bloco D.1 + AUTO-1).
  z.object({
    type: z.literal('celebrar_e_notificar'),
    tituloTemplate: z.string().trim().min(1, 'Título obrigatório').max(120),
    mensagemTemplate: z.string().trim().min(1, 'Mensagem obrigatória').max(280),
    scope: z.enum(['dept', 'workflow', 'global']),
  }),
  z.object({
    type: z.literal('criar_card_workflow'),
    workflowAlvoSlug: z.string().trim().min(1, 'Selecione um workflow alvo'),
    colunaAlvoSlug: z.string().nullable(),
    tituloTemplate: z.string().trim().min(1, 'Título obrigatório').max(200),
    descricaoTemplate: z.string().max(2000).nullable(),
    responsavelEstrategia: responsavelEstrategiaEnum,
    responsavelIdFixo: z.string().nullable(),
    responsavelTag: z.string().nullable(),
    prioridade: prioridadeCanonicaEnum.nullable(),
    copiarFotos: z.boolean(),
    copiarRelato: z.boolean(),
    incluirLinkOrigem: z.boolean(),
  }),
  z.object({
    type: z.literal('setar_prioridade'),
    cardAlvo: cardAlvoEnum,
    novaPrioridade: prioridadeCanonicaEnum,
  }),
  z.object({
    type: z.literal('adicionar_tag'),
    cardAlvo: cardAlvoEnum,
    tag: z.string().trim().min(1, 'Tag obrigatória').max(50),
  }),
  z.object({
    type: z.literal('criar_atividade_card'),
    cardAlvo: cardAlvoEnum,
    activityTypeId: z.string().trim().min(1, 'Selecione um tipo de atividade'),
    descricaoTemplate: z.string().trim().min(1, 'Descrição obrigatória').max(500),
  }),
  z.object({
    type: z.literal('agendar_calendar'),
    calendarUserEstrategia: responsavelEstrategiaEnum,
    calendarUserIdFixo: z.string().nullable(),
    calendarUserTag: z.string().nullable(),
    tituloTemplate: z.string().trim().min(1, 'Título obrigatório').max(200),
    descricaoTemplate: z.string().max(2000).nullable(),
    startModo: z.enum(['offset_minutes', 'data_fixa']),
    startOffsetMinutes: z.coerce.number().int().min(1).max(60 * 24 * 30).nullable(),
    startDataFixaIso: z.string().nullable(),
    duracaoMinutes: z.coerce.number().int().min(1, 'Mínimo 1 min').max(60 * 24),
    attendeesEmails: z.array(z.string().email()).max(20),
  }),
  z.object({
    type: z.literal('webhook_externo'),
    url: z.string().trim().min(1, 'URL obrigatória').max(500),
    method: z.enum(['POST', 'PUT']),
    bodyTemplate: z.string().max(5000),
    headersJson: z.string().max(2000).nullable(),
  }),
]);

const formSchema = z.object({
  nome: z.string().trim().min(3, 'Mínimo 3 caracteres').max(80, 'Máximo 80 caracteres'),
  trigger: triggerSchema,
  actions: z.array(actionSchema).min(1, 'Pelo menos 1 ação').max(20),
  enabled: z.boolean(),
  // FEAT-V2-032: conditions opcional. null/undefined = sem condições.
  conditions: z
    .object({
      logic: z.enum(['AND', 'OR']),
      rules: z.array(
        z.object({
          field: z.string(),
          operator: z.string(),
          value: z.unknown(),
          fieldType: z.string(),
        }),
      ),
    })
    .nullable()
    .optional(),
});

type FormValues = z.infer<typeof formSchema>;

// ─── Helpers ───────────────────────────────────────────────────────────

function autoGenerateName(values: FormValues, labelOf: (s: string) => string): string {
  const t = values.trigger;
  let when = '';
  if (t.type === 'card.entered_column') when = `Quando entra em "${labelOf(t.config.columnSlug)}"`;
  else if (t.type === 'card.left_column') when = `Quando sai de "${labelOf(t.config.columnSlug)}"`;
  else when = `Quando ${t.config.days}d parado em "${labelOf(t.config.columnSlug)}"`;
  const a = values.actions[0];
  let then = '';
  if (a?.type === 'notify_in_app') then = `notificar ${a.target}`;
  else if (a?.type === 'send_whatsapp') then = `WhatsApp para ${a.target}`;
  else if (a?.type === 'send_email') then = `email para ${a.target}`;
  else if (a?.type === 'create_task') then = `criar tarefa D+${a.prazoDiasUteis}d`;
  else if (a?.type === 'move_card') then = `mover pra "${labelOf(a.targetColumnSlug)}"`;
  else if (a?.type === 'assign_responsavel') then = `atribuir via ${a.strategy.replace('_', ' ')}`;
  // BUG-V2-040: 7 actions cross-workflow + celebrar.
  else if (a?.type === 'celebrar_e_notificar') then = `🎉 celebrar (${a.scope})`;
  else if (a?.type === 'criar_card_workflow')
    then = `criar card em ${a.workflowAlvoSlug}`;
  else if (a?.type === 'setar_prioridade') then = `setar prioridade ${a.novaPrioridade}`;
  else if (a?.type === 'adicionar_tag') then = `adicionar tag "${a.tag}"`;
  else if (a?.type === 'criar_atividade_card') then = 'criar atividade no card';
  else if (a?.type === 'agendar_calendar') then = 'agendar evento no Calendar';
  else if (a?.type === 'webhook_externo') then = `webhook → ${a.method} ${a.url}`;
  return `${when}, ${then}`.slice(0, 80);
}

// Sprint 3.1 (2026-05-26): constantes TARGET_LABEL_NOTIFY / _EXTERNAL / _TASK /
// STRATEGY_LABEL movidas pro AcoesEditor compartilhado (única fonte de verdade).

// ─── Componente ────────────────────────────────────────────────────────

export interface WorkflowAutomationFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: Omit<CreateAutomationInput, 'workflowSlug'>) => Promise<void> | void;
  columns: KanbanColumn[];
  /** Deals disponíveis pro sandbox (somente leitura). */
  deals: Deal[];
  /** Automação existente pra edit mode. Se undefined, modo create. */
  initial?: Automation;
  /** ID da automação pra preview no sandbox (precisa ter sido salva). */
  automationId?: string;
  /** Resolve label de coluna (pra nome auto-gerado e options). */
  getColumnLabel: (slug: string) => string;
  /** BUG-V2-058 / Sprint 3: contexto opcional pra metadata + tracking.
   * Pré-seleção real de campos vem via `initial`. */
  contexto?: AutomationContexto;
}

export function WorkflowAutomationForm({
  open,
  onClose,
  onSubmit,
  columns,
  deals,
  initial,
  automationId,
  getColumnLabel,
  contexto: _contexto,
}: WorkflowAutomationFormProps) {
  // `_contexto` reservado pra usos futuros (analytics, telemetria) — caller
  // (shell ou questionario wrapper) é responsável por montar `initial`
  // a partir do contexto se quiser sobrescrever defaults.
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [sandboxOpen, setSandboxOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [autoNameEdited, setAutoNameEdited] = useState(!!initial?.nome);

  const defaultValues: FormValues = useMemo(
    () =>
      initial
        ? {
            nome: initial.nome,
            trigger: initial.trigger as FormValues['trigger'],
            actions: initial.actions as FormValues['actions'],
            enabled: initial.enabled,
            // FEAT-V2-032: carrega conditions se admin já editou antes.
            conditions: (initial as FormValues).conditions ?? null,
          }
        : {
            nome: '',
            trigger: {
              type: 'card.entered_column',
              config: { columnSlug: columns[0]?.slug ?? '' },
            },
            actions: [
              {
                type: 'notify_in_app',
                target: 'responsavel',
                message: 'Card {{cliente}} entrou em {{coluna_atual}}',
              },
            ],
            enabled: true,
            conditions: null,
          },
    [initial, columns]
  );

  // Sprint 3.1 (2026-05-26): captura form completo via `const form` em vez de
  // só destructuring — AcoesEditor compartilhado precisa do UseFormReturn completo.
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema) as Resolver<FormValues>,
    defaultValues,
    mode: 'onChange',
  });
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = form;

  // Reset quando abre/fecha com initial diferente
  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setStep(1);
      setSubmitError(null);
      setAutoNameEdited(!!initial?.nome);
    }
  }, [open, defaultValues, reset, initial?.nome]);

  // Sprint 3.1 (2026-05-26): useFieldArray removido daqui — agora encapsulado
  // dentro do <AcoesEditor> shared. Form usa allValues.actions só pra
  // canAdvanceStep2 + autoGenerateName via watch.
  const triggerType = watch('trigger.type');
  const allValues = watch();

  // Auto-gera nome se admin não editou — dispara só quando a "assinatura"
  // (trigger + 1ª action) muda. Extrair pra string evita expressões
  // complexas no dependency array (eslint-react-hooks/exhaustive-deps).
  const triggerColumnSlug =
    'config' in allValues.trigger ? allValues.trigger.config.columnSlug : '';
  const firstAction = allValues.actions[0];
  const firstActionType = firstAction?.type;
  const firstActionTarget =
    firstAction && 'target' in firstAction ? firstAction.target : undefined;
  useEffect(() => {
    if (!autoNameEdited) {
      const generated = autoGenerateName(allValues, getColumnLabel);
      setValue('nome', generated, { shouldValidate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    autoNameEdited,
    allValues.trigger.type,
    triggerColumnSlug,
    firstActionType,
    firstActionTarget,
  ]);

  const canAdvanceStep1 =
    triggerType !== 'card.idle_in_column' ||
    (typeof (allValues.trigger as { config: { days?: number } }).config.days === 'number' &&
      (allValues.trigger as { config: { days: number } }).config.days >= 1);

  const canAdvanceStep2 =
    allValues.actions.length >= 1 &&
    allValues.actions.every((a) => {
      if (a.type === 'notify_in_app' || a.type === 'send_whatsapp') {
        return !!a.message?.trim();
      }
      if (a.type === 'send_email') {
        return !!a.subject?.trim() && !!a.body?.trim();
      }
      if (a.type === 'create_task') {
        const okText = !!a.texto?.trim() && a.texto.trim().length >= 3;
        const okPrazo =
          typeof a.prazoDiasUteis === 'number' &&
          a.prazoDiasUteis >= 1 &&
          a.prazoDiasUteis <= 60;
        const okTarget =
          a.target !== 'user_specific' || !!a.userId?.trim();
        const okPool =
          a.target !== 'round_robin' || (a.pool && a.pool.length >= 1);
        return okText && okPrazo && okTarget && okPool;
      }
      if (a.type === 'move_card') {
        return !!a.targetColumnSlug?.trim();
      }
      if (a.type === 'assign_responsavel') {
        if (a.strategy === 'fixed') return !!a.userId?.trim();
        return !!a.pool && a.pool.length >= 1;
      }
      // BUG-V2-040: validação mínima das 7 actions novas pra advance.
      if (a.type === 'celebrar_e_notificar') {
        return !!a.tituloTemplate?.trim() && !!a.mensagemTemplate?.trim();
      }
      if (a.type === 'criar_card_workflow') {
        return !!a.workflowAlvoSlug?.trim() && !!a.tituloTemplate?.trim();
      }
      if (a.type === 'setar_prioridade') {
        return !!a.novaPrioridade;
      }
      if (a.type === 'adicionar_tag') {
        return !!a.tag?.trim();
      }
      if (a.type === 'criar_atividade_card') {
        return !!a.activityTypeId?.trim() && !!a.descricaoTemplate?.trim();
      }
      if (a.type === 'agendar_calendar') {
        const okTitulo = !!a.tituloTemplate?.trim();
        const okDuracao = typeof a.duracaoMinutes === 'number' && a.duracaoMinutes >= 1;
        const okStart =
          a.startModo === 'offset_minutes'
            ? typeof a.startOffsetMinutes === 'number' && a.startOffsetMinutes >= 1
            : !!a.startDataFixaIso?.trim();
        return okTitulo && okDuracao && okStart;
      }
      if (a.type === 'webhook_externo') {
        return !!a.url?.trim() && /^https?:\/\//i.test(a.url);
      }
      return false;
    });

  async function handleFormSubmit(values: FormValues) {
    setSubmitError(null);
    try {
      await onSubmit({
        nome: values.nome,
        trigger: values.trigger as AutomationTrigger,
        actions: values.actions as AutomationAction[],
        enabled: values.enabled,
      });
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Erro ao salvar');
    }
  }

  if (!open) return null;

  return (
    <>
      <Dialog open onOpenChange={(o) => !o && onClose()} size="lg">
        <DialogHeader>
          <DialogTitle>
            {initial ? 'Editar automação' : 'Nova automação'} · Passo {step} de 3
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <DialogContent>
            {submitError && (
              <div className="p-3 mb-3 rounded-xl bg-red-soft border-l-2 border-red text-red-deep text-sm">
                {submitError}
              </div>
            )}

            {/* PASSO 1 — Quando */}
            {step === 1 && (
              <div className="space-y-4" data-testid="automation-form-step1">
                <div>
                  <Label>Quando deve disparar?</Label>
                  <div className="grid grid-cols-1 gap-2 mt-2">
                    {[
                      { v: 'card.entered_column', t: 'Quando um card entra em uma coluna' },
                      { v: 'card.left_column', t: 'Quando um card sai de uma coluna' },
                      { v: 'card.idle_in_column', t: 'Quando um card fica parado em uma coluna' },
                    ].map((opt) => (
                      <label
                        key={opt.v}
                        className="flex items-start gap-2 p-2.5 rounded-xl border border-line dark:border-line-dark cursor-pointer hover:bg-paper-warm dark:hover:bg-paper-dark-warm"
                      >
                        <input
                          type="radio"
                          value={opt.v}
                          {...register('trigger.type')}
                          className="mt-0.5"
                        />
                        <span className="text-sm">{opt.t}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="trigger-column">Coluna</Label>
                  <Select
                    id="trigger-column"
                    data-testid="trigger-column"
                    {...register('trigger.config.columnSlug')}
                  >
                    {columns.map((c) => (
                      <option key={c.slug} value={c.slug}>
                        {c.label}
                      </option>
                    ))}
                  </Select>
                </div>

                {triggerType === 'card.idle_in_column' && (
                  <div>
                    <Label htmlFor="trigger-days">Dias parado (mínimo)</Label>
                    <Input
                      id="trigger-days"
                      data-testid="trigger-days"
                      type="number"
                      min={1}
                      max={365}
                      {...register('trigger.config.days')}
                      hint="Scan roda 1x/hora em foreground. Dispara no máx 1x/dia por card."
                    />
                  </div>
                )}
              </div>
            )}

            {/* PASSO 2 — Então (Sprint 3.1: AcoesEditor compartilhado) */}
            {step === 2 && (
              <div className="space-y-4" data-testid="automation-form-step2">
                {/* FEAT-V2-032: bloco de condicionais antes das ações. */}
                <ConditionsBuilder
                  value={(watch('conditions') as ConditionsConfig | null | undefined) ?? null}
                  onChange={(next) => setValue('conditions', next, { shouldDirty: true })}
                  workflowSlug="comercial"
                />
                {/* Sprint 3.1 (2026-05-26): bloco inline de ~800 LOC com 13 actions
                    substituído pelo componente shared AcoesEditor. Tests
                    atualizados pra usar data-testid `acoes-editor-*` em vez de
                    `automation-action-*`/`automation-add-action`. */}
                <AcoesEditor form={form} name="actions" columns={columns} />
              </div>
            )}

            {/* PASSO 3 — Confirmar */}
            {step === 3 && (
              <div className="space-y-4" data-testid="automation-form-step3">
                <div>
                  <Label htmlFor="automation-nome">
                    Nome {!autoNameEdited && '(auto-gerado, editável)'}
                  </Label>
                  <Input
                    id="automation-nome"
                    data-testid="automation-nome"
                    placeholder="Ex: Notificar quando entra em Ganho"
                    error={errors.nome?.message}
                    {...register('nome', {
                      onChange: () => setAutoNameEdited(true),
                    })}
                  />
                </div>

                <div className="rounded-xl bg-paper-warm dark:bg-paper-dark-warm p-3 text-sm space-y-1">
                  <p className="font-medium text-ink-1 dark:text-ink-dark-1">Resumo:</p>
                  <p className="text-ink-2 dark:text-ink-dark-2 italic">
                    {autoGenerateName(allValues, getColumnLabel)}
                  </p>
                  <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-2">
                    {allValues.actions.length} ação(ões) configurada(s)
                  </p>
                </div>

                <Switch
                  label="Ativar imediatamente"
                  checked={allValues.enabled}
                  onChange={(e) => setValue('enabled', e.target.checked)}
                />

                {automationId && (
                  <div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setSandboxOpen(true)}
                    >
                      Testar com card real
                    </Button>
                    <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-1">
                      Preview de tokens resolvidos (não executa actions).
                    </p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            {step > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep((s) => (s === 3 ? 2 : 1))}
              >
                Voltar
              </Button>
            )}
            {step < 3 ? (
              <Button
                type="button"
                data-testid="automation-form-next"
                disabled={step === 1 ? !canAdvanceStep1 : !canAdvanceStep2}
                onClick={() => setStep((s) => (s === 1 ? 2 : 3))}
              >
                Avançar
              </Button>
            ) : (
              <Button
                type="submit"
                data-testid="automation-form-save"
                loading={isSubmitting}
              >
                {initial ? 'Salvar mudanças' : 'Criar automação'}
              </Button>
            )}
          </DialogFooter>
        </form>
      </Dialog>

      {sandboxOpen && automationId && (
        <AutomationSandboxDialog
          open
          onClose={() => setSandboxOpen(false)}
          automationId={automationId}
          deals={deals}
        />
      )}
    </>
  );
}
